import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Music, Play, Pause, SkipForward, Activity } from "lucide-react";
import { cn } from "@/lib/utils";

interface Track {
  name: string;
  artist: string;
  bpm?: number;
  album_art?: string;
}

interface CompactMusicPlayerProps {
  currentTrack: Track | null;
  isPlaying: boolean;
  currentBPM: number;
  isSynced: boolean;
  isAdjusting?: boolean;
  onPlayPause: () => void;
  onNext: () => void;
  className?: string;
}

export const CompactMusicPlayer = ({
  currentTrack,
  isPlaying,
  currentBPM,
  isSynced,
  isAdjusting = false,
  onPlayPause,
  onNext,
  className
}: CompactMusicPlayerProps) => {
  if (!currentTrack) {
    return (
      <Card className={cn("p-3 bg-background/95 backdrop-blur-sm border-border/50", className)}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
            <Music className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground">Nenhuma música tocando</p>
          </div>
        </div>
      </Card>
    );
  }

  const getSyncStatus = () => {
    if (isAdjusting) return { text: 'Ajustando...', color: 'text-blue-500' };
    if (isSynced) return { text: 'Sincronizado', color: 'text-green-500' };
    return { text: 'Buscando...', color: 'text-orange-500' };
  };

  const status = getSyncStatus();

  return (
    <Card className={cn("p-3 bg-background/95 backdrop-blur-sm border-border/50 shadow-lg", className)}>
      <div className="flex items-center gap-3">
        {/* Album Art */}
        <div className="w-10 h-10 rounded bg-muted flex-shrink-0 overflow-hidden" role="img" aria-label={`Capa do álbum: ${currentTrack.name}`}>
          {currentTrack.album_art ? (
            <img 
              src={currentTrack.album_art} 
              alt={`Capa do álbum de ${currentTrack.name}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Music className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
            </div>
          )}
        </div>

        {/* Track Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{currentTrack.name}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="truncate">{currentTrack.artist}</span>
            {currentTrack.bpm && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Activity className="w-3 h-3" />
                  {currentTrack.bpm} BPM
                </span>
              </>
            )}
          </div>
          {currentBPM > 0 && (
            <div 
              className={cn("flex items-center gap-1 text-xs mt-0.5", status.color)}
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              <div className={cn("w-1.5 h-1.5 rounded-full", isAdjusting && "animate-pulse")} aria-hidden="true">
                <div className={cn(
                  "w-full h-full rounded-full",
                  isSynced ? "bg-green-500" : isAdjusting ? "bg-blue-500" : "bg-orange-500"
                )} />
              </div>
              <span>{status.text}</span>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={onPlayPause}
            className="h-8 w-8 p-0"
            aria-label={isPlaying ? 'Pausar música' : 'Reproduzir música'}
            aria-pressed={isPlaying}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4" aria-hidden="true" />
            ) : (
              <Play className="w-4 h-4" aria-hidden="true" />
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onNext}
            className="h-8 w-8 p-0"
            aria-label="Próxima música"
          >
            <SkipForward className="w-4 h-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </Card>
  );
};
